import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import Modal from '../components/Modal';
import * as XLSX from 'xlsx';

const fmt = (v) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
const dateLabel = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SumCard = ({ icon, label, value, sub, color, textColor }) => (
    <div className={`rounded-2xl p-5 ${color} border border-white/50 shadow-sm`}>
        <div className="flex items-start justify-between">
            <div>
                <p className={`text-xs font-bold uppercase tracking-widest ${textColor} opacity-70`}>{label}</p>
                <p className={`text-2xl font-black mt-1 ${textColor}`}>CFA {fmt(value)}</p>
                {sub && <p className={`text-xs mt-1 ${textColor} opacity-60`}>{sub}</p>}
            </div>
            <span className="text-3xl">{icon}</span>
        </div>
    </div>
);

// ─── Print Styles ─────────────────────────────────────────────────────────────
const PRINT_STYLES = `
@media print {
    body > *:not(#ledger-print-root) { display: none !important; }
    #ledger-print-root { display: block !important; }
    @page { margin: 15mm; size: A4; }
}
`;

// ─── Main Component ───────────────────────────────────────────────────────────
const JournalEntry = () => {
    const { authFetch, user, depos, selectedDepo } = useContext(AppContext);
    const { t } = useTranslation();
    const isAdmin = user?.role === 'Admin' || user?.role === 'Manager' || user?.role === 'Controller';
    const printRef = useRef(null);

    const today = new Date().toISOString().slice(0, 10);
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

    const [filterStart, setFilterStart] = useState(firstOfMonth);
    const [filterEnd, setFilterEnd] = useState(today);
    const [depoFilter, setDepoFilter] = useState(selectedDepo !== 'Global' ? selectedDepo : '');
    const [loading, setLoading] = useState(false);

    const [ledgerRows, setLedgerRows] = useState([]);
    const [journalEntries, setJournalEntries] = useState([]);
    const [totals, setTotals] = useState({});
    const [expandedRow, setExpandedRow] = useState(null);
    const [activeTab, setActiveTab] = useState('ledger');

    // ── Edit modals ──
    const [editModal, setEditModal] = useState(null);   // { type: 'purchases'|'expenses', day, data[] }
    const [editLoading, setEditLoading] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null); // expense being inline-edited
    const [printRow, setPrintRow] = useState(null);      // single-row print

    // ── Manual journal entry ──
    const [accounts, setAccounts] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [entryDate, setEntryDate] = useState(today);
    const [reference, setReference] = useState('');
    const [description, setDescription] = useState('');
    const [lines, setLines] = useState([
        { account_id: '', debit: 0, credit: 0 },
        { account_id: '', debit: 0, credit: 0 }
    ]);

    // ── Fetch main ledger ─────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ start: filterStart, end: filterEnd });
            if (depoFilter) params.append('depo_id', depoFilter);
            const [ledgerRes, acctRes] = await Promise.all([
                authFetch(`/api/daily_ledger?${params}`),
                authFetch('/api/accounts')
            ]);
            const ledgerData = await ledgerRes.json();
            const acctData = await acctRes.json();
            setLedgerRows(Array.isArray(ledgerData.rows) ? ledgerData.rows : []);
            setJournalEntries(Array.isArray(ledgerData.journal_entries) ? ledgerData.journal_entries : []);
            setTotals(ledgerData.totals || {});
            setAccounts(Array.isArray(acctData) ? acctData : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [authFetch, filterStart, filterEnd, depoFilter]);

    useEffect(() => { fetchData(); }, [filterStart, filterEnd, depoFilter]);

    // ── Open Edit Modal (real data) ───────────────────────────────────────────
    const openEditModal = async (type, day) => {
        setEditLoading(true);
        setEditModal({ type, day, data: [] });
        try {
            let data = [];
            if (type === 'purchases') {
                const res = await authFetch(`/api/purchases?start_date=${day}&end_date=${day}&detailed=1`);
                data = await res.json();
            } else if (type === 'expenses') {
                const res = await authFetch(`/api/expenses?date=${day}`);
                data = await res.json();
            }
            setEditModal({ type, day, data: Array.isArray(data) ? data : [] });
        } catch (e) { console.error(e); }
        finally { setEditLoading(false); }
    };

    // ── Delete expense ────────────────────────────────────────────────────────
    const handleDeleteExpense = async (id) => {
        if (!window.confirm(t('Delete this expense?'))) return;
        await authFetch(`/api/expenses/${id}`, { method: 'DELETE' });
        setEditModal(prev => ({ ...prev, data: prev.data.filter(e => e.id !== id) }));
        fetchData();
    };

    // ── Save inline expense edit ──────────────────────────────────────────────
    const handleSaveExpense = async (exp) => {
        try {
            await authFetch(`/api/expenses/${exp.id}`, {
                method: 'PUT',
                body: JSON.stringify({ category: exp.category, amount: exp.amount, description: exp.description })
            });
            setEditModal(prev => ({ ...prev, data: prev.data.map(e => e.id === exp.id ? exp : e) }));
            setEditingExpense(null);
            fetchData();
        } catch (e) { alert(t('Save failed')); }
    };

    // ── Post journal entry ────────────────────────────────────────────────────
    const totalDebit = lines.reduce((s, l) => s + parseFloat(l.debit || 0), 0);
    const totalCredit = lines.reduce((s, l) => s + parseFloat(l.credit || 0), 0);
    const isBalanced = totalDebit > 0 && Math.abs(totalDebit - totalCredit) < 0.01;

    const handlePost = async (e) => {
        e.preventDefault();
        if (!isBalanced) return alert(t('Entry must be balanced!'));
        const payload = { date: entryDate, reference, description, items: lines.filter(l => l.account_id).map(l => ({ account_id: l.account_id, debit: parseFloat(l.debit || 0), credit: parseFloat(l.credit || 0) })) };
        const res = await authFetch('/api/journal', { method: 'POST', body: JSON.stringify(payload) });
        if (res.ok) {
            setIsAddModalOpen(false);
            setLines([{ account_id: '', debit: 0, credit: 0 }, { account_id: '', debit: 0, credit: 0 }]);
            setReference(''); setDescription('');
            fetchData();
        } else { const d = await res.json(); alert(d.message); }
    };

    // ── Print All ─────────────────────────────────────────────────────────────
    const handlePrintAll = () => {
        setPrintRow(null);
        setTimeout(() => window.print(), 300);
    };

    // ── Print single row ──────────────────────────────────────────────────────
    const handlePrintRow = (row, e) => {
        e.stopPropagation();
        setPrintRow(row);
        setTimeout(() => window.print(), 300);
    };

    // ── Export Excel ──────────────────────────────────────────────────────────
    const handleExport = () => {
        const rows = ledgerRows.map(r => ({
            'Date': r.day,
            'Cash Sales (CFA)': r.cash_sales,
            'Cash Collected (CFA)': r.cash_collected,
            'Purchases / Achats (CFA)': r.total_purchases,
            'Expenses / Dépenses (CFA)': r.total_expenses,
            'Cash In (Debit)': r.debit,
            'Cash Out (Credit)': r.credit,
            'Net Balance (CFA)': r.net_cash,
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Daily Ledger');
        XLSX.writeFile(wb, `Daily_Ledger_${filterStart}_to_${filterEnd}.xlsx`);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // PRINT VIEW (hidden on screen, visible only on print)
    // ─────────────────────────────────────────────────────────────────────────
    const rowsToPrint = printRow ? [printRow] : ledgerRows;
    const totalsToPrint = printRow
        ? { total_debit: printRow.debit, total_credit: printRow.credit, net_balance: printRow.net_cash, total_cash_sales: printRow.cash_sales, total_purchases: printRow.total_purchases, total_expenses: printRow.total_expenses }
        : totals;

    const PrintView = () => (
        <div id="ledger-print-root" className="hidden print:block bg-white text-black p-8 font-sans">
            {/* Header */}
            {/* Header */}
            <div className="flex gap-4 items-center mb-6 border-b-2 border-[#1a4f8a] pb-4">
                <div className="w-24 h-24 border-[3px] border-[#3eb56b] rounded-full flex flex-col items-center justify-center font-bold text-xs bg-white text-[#1a4f8a] overflow-hidden">
                    <div className="text-4xl">⮏⮎</div>
                </div>
                <div>
                    <h1 className="text-5xl font-black text-[#1a4f8a] tracking-tight m-0">Yazbek</h1>
                    <p className="text-lg text-gray-700 m-0 leading-tight">Societe d'importation et de commerce</p>
                    <h2 className="text-2xl font-bold tracking-widest mt-2 uppercase text-[#1a4f8a] flex items-center gap-2">
                        SUPERMARCHE Yazbek 🛒
                    </h2>
                </div>
            </div>

            <h2 className="text-3xl font-bold mb-4 font-serif">Daily Ledger & Journal Report</h2>

            <div className="w-1/2 text-xs font-semibold uppercase mb-8 bg-gray-50 border-y border-black p-2">
                <div className="grid grid-cols-2 py-1"><span className="text-gray-500">PERIOD</span> <span>{printRow ? printRow.day : `${filterStart} — ${filterEnd}`}</span></div>
                <div className="grid grid-cols-2 py-1"><span className="text-gray-500">REPORT DATE</span> <span>{new Date().toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '')}</span></div>
                <div className="grid grid-cols-2 py-1"><span className="text-gray-500">BRANCH</span> <span>{depoFilter ? (depos.find(d => String(d.id) === String(depoFilter))?.name || depoFilter) : 'ALL BRANCHES'}</span></div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                    { label: 'Cash Sales', val: totalsToPrint.total_cash_sales },
                    { label: 'Purchases', val: totalsToPrint.total_purchases },
                    { label: 'Expenses', val: totalsToPrint.total_expenses },
                    { label: 'Net Balance', val: totalsToPrint.net_balance },
                ].map(c => (
                    <div key={c.label} className="border rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-400 uppercase font-bold">{c.label}</p>
                        <p className={`text-lg font-black mt-1 ${c.label === 'Net Balance' ? (c.val >= 0 ? 'text-green-700' : 'text-red-700') : 'text-gray-900'}`}>CFA {fmt(c.val)}</p>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="w-full overflow-x-auto max-w-full">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-indigo-700 text-white">
                            <th className="p-2 text-left">Date</th>
                            <th className="p-2 text-left">Transactions</th>
                            <th className="p-2 text-right">Cash Sales</th>
                            <th className="p-2 text-right">Purchases</th>
                            <th className="p-2 text-right">Expenses</th>
                            <th className="p-2 text-right">Cash In</th>
                            <th className="p-2 text-right">Cash Out</th>
                            <th className="p-2 text-right">Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rowsToPrint.map((r, i) => (
                            <tr key={r.day} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="p-2 font-bold border border-gray-200">{r.day}</td>
                                <td className="p-2 border border-gray-200 text-xs">
                                    {r.invoice_count > 0 && <span className="mr-2">🛒{r.invoice_count} Sales</span>}
                                    {r.purchase_count > 0 && <span className="mr-2">📦{r.purchase_count} POs</span>}
                                    {r.expense_count > 0 && <span>💸{r.expense_count} Exp</span>}
                                </td>
                                <td className="p-2 text-right border border-gray-200">{fmt(r.cash_sales)}</td>
                                <td className="p-2 text-right border border-gray-200">{fmt(r.total_purchases)}</td>
                                <td className="p-2 text-right border border-gray-200">{fmt(r.total_expenses)}</td>
                                <td className="p-2 text-right border border-gray-200 text-green-700 font-bold">{fmt(r.debit)}</td>
                                <td className="p-2 text-right border border-gray-200 text-red-600 font-bold">{fmt(r.credit)}</td>
                                <td className={`p-2 text-right border border-gray-200 font-black ${r.net_cash >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmt(r.net_cash)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-gray-100 font-black">
                            <td colSpan="5" className="p-2 border border-gray-200">TOTAL</td>
                            <td className="p-2 text-right border border-gray-200 text-green-700">CFA {fmt(totalsToPrint.total_debit)}</td>
                            <td className="p-2 text-right border border-gray-200 text-red-600">CFA {fmt(totalsToPrint.total_credit)}</td>
                            <td className={`p-2 text-right border border-gray-200 ${totalsToPrint.net_balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>CFA {fmt(totalsToPrint.net_balance)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // SCREEN VIEW
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <style>{PRINT_STYLES}</style>
            <PrintView />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/20 to-purple-50/10 p-6 space-y-6 no-print">

                {/* ── Header ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-gray-900 tracking-tight">{t('Daily Ledger')}</h1>
                            <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest">{t('Sales · Purchases · Expenses')}</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2">
                            <svg className="w-4 h-4 text-indigo-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            <input type="date" className="bg-transparent border-0 text-sm font-bold text-gray-700 outline-none" value={filterStart} onChange={e => setFilterStart(e.target.value)} />
                            <span className="text-gray-300">→</span>
                            <input type="date" className="bg-transparent border-0 text-sm font-bold text-gray-700 outline-none" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} />
                        </div>

                        <select value={depoFilter} onChange={e => setDepoFilter(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold text-gray-700 outline-none">
                            <option value="">{t('All Branches')}</option>
                            {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>

                        {/* Print All */}
                        <button onClick={handlePrintAll} className="p-2.5 bg-gray-800 text-white rounded-xl hover:bg-black shadow-md transition-all" title={t('Print All')}>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                        </button>

                        {/* Export */}
                        <button onClick={handleExport} className="p-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 shadow-md transition-all" title={t('Export Excel')}>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        </button>

                        {isAdmin && (
                            <button onClick={() => setIsAddModalOpen(true)} className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all">
                                + {t('Add Journal Entry')}
                            </button>
                        )}
                    </div>
                </div>

                {/* ── Summary Cards ── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <SumCard icon="💵" label={t('Cash Sales')} value={totals.total_cash_sales} sub={`Collected: CFA ${fmt(totals.total_cash_collected)}`} color="bg-blue-50" textColor="text-blue-900" />
                    <SumCard icon="📦" label={t('Total Purchases')} value={totals.total_purchases} color="bg-amber-50" textColor="text-amber-900" />
                    <SumCard icon="💸" label={t('Total Expenses')} value={totals.total_expenses} color="bg-red-50" textColor="text-red-900" />
                    <SumCard
                        icon={totals.net_balance >= 0 ? '📈' : '📉'}
                        label={t('Net Balance')}
                        value={totals.net_balance}
                        sub={`In: ${fmt(totals.total_debit)} | Out: ${fmt(totals.total_credit)}`}
                        color={totals.net_balance >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}
                        textColor={totals.net_balance >= 0 ? 'text-emerald-900' : 'text-rose-900'}
                    />
                </div>

                {/* ── Tabs ── */}
                <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
                    {[['ledger', '📋 Daily Ledger'], ['journal', '📝 Journal Entries']].map(([id, label]) => (
                        <button key={id} onClick={() => setActiveTab(id)}
                            className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === id ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
                            {label}
                        </button>
                    ))}
                </div>

                {/* ── DAILY LEDGER TAB ── */}
                {activeTab === 'ledger' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        {/* Column Headers */}
                        <div className="flex items-center px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-bold uppercase tracking-wider gap-4">
                            <span className="w-24 flex-shrink-0">Date</span>
                            <span className="flex-1">Transactions</span>
                            <span className="w-32 text-right flex-shrink-0">Cash In (Dr)</span>
                            <span className="w-32 text-right flex-shrink-0">Cash Out (Cr)</span>
                            <span className="w-36 text-right flex-shrink-0">Net Balance</span>
                            <span className="w-20 text-right flex-shrink-0">Actions</span>
                        </div>

                        <div className="p-4">
                            {loading ? (
                                <div className="py-20 text-center text-indigo-500 flex items-center justify-center gap-2">
                                    <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                    Loading...
                                </div>
                            ) : ledgerRows.length === 0 ? (
                                <div className="py-20 text-center">
                                    <p className="text-5xl mb-3">📭</p>
                                    <p className="text-gray-400 font-semibold">{t('No transactions in this period')}</p>
                                </div>
                            ) : ledgerRows.map(row => {
                                const isPositive = row.net_cash >= 0;
                                const isExpanded = expandedRow === row.day;
                                return (
                                    <div key={row.day} className={`rounded-xl border mb-2 overflow-hidden transition-all ${isPositive ? 'border-emerald-100' : 'border-red-100'}`}>
                                        {/* Row Header */}
                                        <div
                                            className={`flex items-center gap-4 px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-indigo-50/40' : 'bg-white'}`}
                                            onClick={() => setExpandedRow(isExpanded ? null : row.day)}
                                        >
                                            <div className="w-24 flex-shrink-0">
                                                <p className="font-black text-gray-800 text-sm">{new Date(row.day + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
                                                <p className="text-xs text-gray-400">{new Date(row.day + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' })}</p>
                                            </div>

                                            <div className="flex-1 flex flex-wrap gap-2">
                                                {row.invoice_count > 0 && <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-xs font-semibold">🛒 {row.invoice_count} Sales</span>}
                                                {row.purchase_count > 0 && <span className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-100 rounded-full text-xs font-semibold">📦 {row.purchase_count} Purchases</span>}
                                                {row.expense_count > 0 && <span className="px-2.5 py-1 bg-red-50 text-red-700 border border-red-100 rounded-full text-xs font-semibold">💸 {row.expense_count} Expenses</span>}
                                            </div>

                                            <div className="w-32 text-right flex-shrink-0">
                                                <p className="text-xs text-gray-400 font-semibold">Cash In</p>
                                                <p className="font-bold text-emerald-600">{fmt(row.debit)}</p>
                                            </div>
                                            <div className="w-32 text-right flex-shrink-0">
                                                <p className="text-xs text-gray-400 font-semibold">Cash Out</p>
                                                <p className="font-bold text-red-500">{fmt(row.credit)}</p>
                                            </div>
                                            <div className="w-36 text-right flex-shrink-0">
                                                <p className="text-xs text-gray-400 font-semibold">Balance</p>
                                                <p className={`font-black text-lg ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                                                    {isPositive ? '+' : ''}{fmt(row.net_cash)}
                                                </p>
                                            </div>

                                            {/* Per-row print button */}
                                            <div className="w-20 flex items-center justify-end gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                                <button
                                                    onClick={(e) => handlePrintRow(row, e)}
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                                    title={t('Print this day')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                                </button>
                                                <span className="text-gray-300 text-sm">{isExpanded ? '▲' : '▼'}</span>
                                            </div>
                                        </div>

                                        {/* Expanded Detail */}
                                        {isExpanded && (
                                            <div className="border-t border-gray-100 px-5 py-4 bg-white">
                                                <div className="grid grid-cols-3 gap-4">
                                                    {/* Cash Sales card — view only */}
                                                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-lg">🛒</span>
                                                                <span className="text-xs font-bold text-blue-700 uppercase">Cash Sales</span>
                                                            </div>
                                                        </div>
                                                        <p className="text-xl font-black text-blue-800">CFA {fmt(row.cash_sales)}</p>
                                                        <p className="text-xs text-blue-600 mt-1">Collected: CFA {fmt(row.cash_collected)}</p>
                                                        <p className="text-xs text-blue-400">{row.invoice_count} invoices</p>
                                                    </div>

                                                    {/* Purchases card — with Edit */}
                                                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-lg">📦</span>
                                                                <span className="text-xs font-bold text-amber-700 uppercase">Purchases</span>
                                                            </div>
                                                            <button
                                                                onClick={() => openEditModal('purchases', row.day)}
                                                                className="flex items-center gap-1 text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 font-semibold px-2 py-1 rounded-lg transition-colors"
                                                            >
                                                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                                View / Edit
                                                            </button>
                                                        </div>
                                                        <p className="text-xl font-black text-amber-800">CFA {fmt(row.total_purchases)}</p>
                                                        <p className="text-xs text-amber-400">{row.purchase_count} purchase orders</p>
                                                    </div>

                                                    {/* Expenses card — with Edit */}
                                                    <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-lg">💸</span>
                                                                <span className="text-xs font-bold text-red-700 uppercase">Expenses</span>
                                                            </div>
                                                            <button
                                                                onClick={() => openEditModal('expenses', row.day)}
                                                                className="flex items-center gap-1 text-xs bg-red-100 hover:bg-red-200 text-red-800 font-semibold px-2 py-1 rounded-lg transition-colors"
                                                            >
                                                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                                View / Edit
                                                            </button>
                                                        </div>
                                                        <p className="text-xl font-black text-red-800">CFA {fmt(row.total_expenses)}</p>
                                                        <p className="text-xs text-red-400">{row.expense_count} entries</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer totals */}
                        {ledgerRows.length > 0 && (
                            <div className="border-t border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50 px-5 py-4">
                                <div className="flex items-center justify-end gap-8 text-sm font-bold">
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 uppercase">Total Cash In</p>
                                        <p className="text-emerald-600 text-lg">CFA {fmt(totals.total_debit)}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 uppercase">Total Cash Out</p>
                                        <p className="text-red-500 text-lg">CFA {fmt(totals.total_credit)}</p>
                                    </div>
                                    <div className="text-center border-l border-gray-200 pl-8">
                                        <p className="text-xs text-gray-400 uppercase">Net</p>
                                        <p className={`text-xl font-black ${totals.net_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>CFA {fmt(totals.net_balance)}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── JOURNAL TAB ── */}
                {activeTab === 'journal' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-5 py-3 bg-gradient-to-r from-slate-700 to-gray-800 text-white text-xs font-bold uppercase tracking-wider grid grid-cols-[100px_120px_1fr_1fr_100px] gap-4">
                            <span>Date</span><span>Reference</span><span>Description</span><span>Accounts</span><span className="text-right">Amount</span>
                        </div>
                        <div className="divide-y divide-gray-50">
                            {journalEntries.length === 0 ? (
                                <div className="py-16 text-center text-gray-400">
                                    <p className="text-4xl mb-2">📝</p>
                                    <p className="font-medium">{t('No manual entries in this period')}</p>
                                    <button onClick={() => setIsAddModalOpen(true)} className="mt-3 text-indigo-600 text-sm font-semibold hover:underline">+ {t('Add one now')}</button>
                                </div>
                            ) : journalEntries.map(je => (
                                <div key={je.id} className="px-5 py-4 hover:bg-indigo-50/30 transition-colors grid grid-cols-[100px_120px_1fr_1fr_100px] gap-4 items-start">
                                    <span className="font-bold text-gray-700 text-sm">{new Date(je.date + 'T00:00:00').toLocaleDateString('en-GB')}</span>
                                    <span className="text-indigo-600 font-mono text-xs font-semibold">{je.reference || '—'}</span>
                                    <span className="text-gray-700 text-sm">{je.description}</span>
                                    <div className="text-xs space-y-1">
                                        {je.items?.map((item, i) => (
                                            <div key={i} className="flex justify-between">
                                                <span className="text-gray-500 font-mono">{item.code} {item.account_name}</span>
                                                <div className="flex gap-3">
                                                    {parseFloat(item.debit) > 0 && <span className="text-indigo-600 font-bold">{fmt(item.debit)} DR</span>}
                                                    {parseFloat(item.credit) > 0 && <span className="text-gray-700 font-bold">{fmt(item.credit)} CR</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-gray-800">{fmt(je.total_debit)}</p>
                                        <p className="text-xs text-gray-400">{je.creator_name}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════
                EDIT MODAL — Real Purchase List / Expense List
            ═══════════════════════════════════════════════════════════════ */}
                {editModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-end" onClick={() => { setEditModal(null); setEditingExpense(null); }}>
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                        <div
                            className="relative w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto flex flex-col"
                            style={{ animation: 'slideInRight 0.25s ease-out' }}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Drawer Header */}
                            <div className={`p-6 flex items-start justify-between flex-shrink-0 ${editModal.type === 'purchases' ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
                                <div>
                                    <p className="text-white/70 text-xs font-semibold uppercase tracking-widest">
                                        {editModal.type === 'purchases' ? '📦 Purchase Orders' : '💸 Expense Entries'}
                                    </p>
                                    <h2 className="text-2xl font-black text-white mt-1">{dateLabel(editModal.day)}</h2>
                                    <p className="text-white/70 text-xs mt-1">{editModal.data.length} {editModal.type === 'purchases' ? 'purchase orders' : 'expense entries'}</p>
                                </div>
                                <button onClick={() => { setEditModal(null); setEditingExpense(null); }} className="text-white/70 hover:text-white p-1 transition-colors">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            {/* Drawer Body */}
                            <div className="flex-1 p-6 space-y-3">
                                {editLoading && (
                                    <div className="py-20 text-center text-gray-400">
                                        <svg className="animate-spin w-8 h-8 mx-auto text-indigo-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                        <p className="mt-3 font-medium">Loading records...</p>
                                    </div>
                                )}

                                {!editLoading && editModal.data.length === 0 && (
                                    <div className="py-20 text-center text-gray-400">
                                        <p className="text-4xl mb-2">📭</p>
                                        <p className="font-medium">No records found for this day</p>
                                    </div>
                                )}

                                {/* ── PURCHASES LIST ── */}
                                {!editLoading && editModal.type === 'purchases' && editModal.data.map(p => (
                                    <div key={p.id} className="border border-amber-100 rounded-xl overflow-hidden bg-white shadow-sm">
                                        <div className="bg-amber-50 px-4 py-3 flex items-center justify-between">
                                            <div>
                                                <p className="font-black text-amber-900">#{p.invoice_number || p.id}</p>
                                                <p className="text-xs text-amber-600">{p.supplier_name || p.supplier || 'N/A'} · {p.depo_name || ''}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${p.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                                                <button
                                                    onClick={() => window.open(`/purchases/edit/${p.id}`, '_blank')}
                                                    className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 transition-colors flex items-center gap-1"
                                                >
                                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                    Edit
                                                </button>
                                            </div>
                                        </div>
                                        {/* Items list */}
                                        {p.items && p.items.length > 0 && (
                                            <div className="px-4 py-3">
                                                <div className="w-full overflow-x-auto max-w-full">
                                                    <table className="w-full text-xs">
                                                        <thead>
                                                            <tr className="text-gray-400 uppercase">
                                                                <th className="text-left pb-2">Product</th>
                                                                <th className="text-center pb-2">Qty</th>
                                                                <th className="text-right pb-2">Unit Cost</th>
                                                                <th className="text-right pb-2">Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-50">
                                                            {p.items.map((item, i) => {
                                                                const qty = parseFloat(item.quantity || item.qty || 0);
                                                                const cost = parseFloat(item.unit_price || item.cost_price || 0);
                                                                return (
                                                                    <tr key={i}>
                                                                        <td className="py-1.5 font-medium text-gray-700">{item.product_name}</td>
                                                                        <td className="py-1.5 text-center text-gray-500">{qty}</td>
                                                                        <td className="py-1.5 text-right text-gray-500">{fmt(cost)}</td>
                                                                        <td className="py-1.5 text-right font-bold text-gray-800">{fmt(qty * cost)}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className="border-t border-gray-100 mt-2 pt-2 flex justify-end">
                                                    <span className="font-black text-amber-800">Total: CFA {fmt(p.total_cost || p.grand_total)}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* ── EXPENSES LIST ── */}
                                {!editLoading && editModal.type === 'expenses' && editModal.data.map(exp => (
                                    <div key={exp.id} className="border border-red-100 rounded-xl overflow-hidden bg-white shadow-sm">
                                        {editingExpense?.id === exp.id ? (
                                            /* Inline Edit Form */
                                            <div className="p-4 bg-red-50 space-y-3">
                                                <p className="text-xs font-bold text-red-700 uppercase mb-2">✏️ Editing Expense #{exp.id}</p>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-xs font-bold text-gray-600">Category</label>
                                                        <select
                                                            className="w-full border rounded-lg p-2 text-sm mt-1 bg-white"
                                                            value={editingExpense.category}
                                                            onChange={e => {
                                                                if (e.target.value === '__ADD_NEW__') {
                                                                    const newCat = window.prompt('Enter new expense category name:');
                                                                    if (newCat) {
                                                                        authFetch('/api/accounts', { method: 'POST', body: JSON.stringify({ code: '', name: newCat, type: 'Expense' }) }).then(() => fetchData());
                                                                        setEditingExpense(prev => ({ ...prev, category: newCat }));
                                                                    }
                                                                } else {
                                                                    setEditingExpense(prev => ({ ...prev, category: e.target.value }));
                                                                }
                                                            }}
                                                        >
                                                            <option value="">-- Select Category --</option>
                                                            {accounts.filter(a => a.type === 'Expense').map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                                                            {editingExpense.category && !accounts.some(a => a.type === 'Expense' && a.name === editingExpense.category) && (
                                                                <option value={editingExpense.category}>{editingExpense.category}</option>
                                                            )}
                                                            <option value="__ADD_NEW__">+ Add New Category</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-bold text-gray-600">Amount (CFA)</label>
                                                        <input
                                                            type="number" step="any"
                                                            className="w-full border rounded-lg p-2 text-sm mt-1 bg-white font-bold"
                                                            value={editingExpense.amount}
                                                            onChange={e => setEditingExpense(prev => ({ ...prev, amount: e.target.value }))}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-600">Description</label>
                                                    <input
                                                        className="w-full border rounded-lg p-2 text-sm mt-1 bg-white"
                                                        value={editingExpense.description || ''}
                                                        onChange={e => setEditingExpense(prev => ({ ...prev, description: e.target.value }))}
                                                    />
                                                </div>
                                                <div className="flex gap-2 pt-1">
                                                    <button onClick={() => handleSaveExpense(editingExpense)} className="flex-1 bg-emerald-600 text-white rounded-lg py-2 text-sm font-bold hover:bg-emerald-700">Save Changes</button>
                                                    <button onClick={() => setEditingExpense(null)} className="px-4 bg-gray-200 text-gray-700 rounded-lg py-2 text-sm font-bold hover:bg-gray-300">Cancel</button>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Normal Display */
                                            <div className="flex items-center justify-between px-4 py-3">
                                                <div>
                                                    <p className="font-bold text-gray-800 text-sm">{exp.category}</p>
                                                    {exp.description && <p className="text-xs text-gray-400 mt-0.5">{exp.description}</p>}
                                                    <p className="text-xs text-gray-400 mt-1">{new Date(exp.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <p className="font-black text-red-700 text-lg">CFA {fmt(exp.amount)}</p>
                                                    <button
                                                        onClick={() => setEditingExpense({ ...exp })}
                                                        className="p-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteExpense(exp.id)}
                                                        className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Drawer Footer */}
                            <div className="border-t border-gray-100 p-4 bg-gray-50 flex-shrink-0">
                                <div className="flex justify-between items-center">
                                    <p className="text-xs text-gray-400 font-semibold uppercase">
                                        {editModal.data.length} {editModal.type === 'purchases' ? 'purchase orders' : 'expense entries'}
                                    </p>
                                    <p className="font-black text-lg text-gray-800">
                                        Total: CFA {fmt(
                                            editModal.type === 'purchases'
                                                ? editModal.data.reduce((s, p) => s + parseFloat(p.total_cost || p.grand_total || 0), 0)
                                                : editModal.data.reduce((s, e) => s + parseFloat(e.amount || 0), 0)
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Add Journal Entry Modal ── */}
                <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t('New Journal Entry')} size="6xl">
                    <form onSubmit={handlePost} className="space-y-4">
                        <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-xl border">
                            <div>
                                <label className="block text-sm font-bold text-gray-700">{t('Date')}</label>
                                <input required type="date" className="w-full border p-2 rounded mt-1" value={entryDate} onChange={e => setEntryDate(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700">{t('Reference')}</label>
                                <input placeholder="e.g. INV-2024-001" className="w-full border p-2 rounded mt-1" value={reference} onChange={e => setReference(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700">{t('Description')} *</label>
                                <input required placeholder={t('Transaction details...')} className="w-full border p-2 rounded mt-1" value={description} onChange={e => setDescription(e.target.value)} />
                            </div>
                        </div>
                        <div className="border rounded-xl overflow-hidden shadow-sm">
                            <div className="w-full overflow-x-auto max-w-full">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-bold">
                                        <tr>
                                            <th className="p-3 w-1/2">{t('Account')}</th>
                                            <th className="p-3 w-1/5 text-right text-indigo-700">{t('Debit (DR)')}</th>
                                            <th className="p-3 w-1/5 text-right">{t('Credit (CR)')}</th>
                                            <th className="p-3 w-8"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.map((line, idx) => (
                                            <tr key={idx} className="border-t">
                                                <td className="p-2">
                                                    <select required className="w-full border p-2 rounded" value={line.account_id} onChange={e => { const l = [...lines]; l[idx].account_id = e.target.value; setLines(l); }}>
                                                        <option value="">-- {t('Select Account')} --</option>
                                                        {accounts.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name} ({a.type})</option>)}
                                                    </select>
                                                </td>
                                                <td className="p-2"><input type="number" min="0" step="any" className="w-full border p-2 rounded text-right text-indigo-700 font-bold bg-indigo-50" value={line.debit} onChange={e => { const l = [...lines]; l[idx].debit = e.target.value; setLines(l); }} /></td>
                                                <td className="p-2"><input type="number" min="0" step="any" className="w-full border p-2 rounded text-right font-bold bg-gray-50" value={line.credit} onChange={e => { const l = [...lines]; l[idx].credit = e.target.value; setLines(l); }} /></td>
                                                <td className="p-2 text-center"><button type="button" onClick={() => { if (lines.length > 2) setLines(lines.filter((_, i) => i !== idx)); }} className="text-red-400 hover:text-red-600 font-bold text-xl">×</button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-50 font-bold">
                                        <tr>
                                            <td className="p-3"><button type="button" onClick={() => setLines([...lines, { account_id: '', debit: 0, credit: 0 }])} className="text-indigo-600 text-sm hover:underline">+ {t('Add Line')}</button></td>
                                            <td className={`p-3 text-right text-lg ${!isBalanced && totalDebit > 0 ? 'text-red-500' : 'text-indigo-700'}`}>{fmt(totalDebit)}</td>
                                            <td className={`p-3 text-right text-lg ${!isBalanced && totalCredit > 0 ? 'text-red-500' : 'text-gray-800'}`}>{fmt(totalCredit)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                        {!isBalanced && (totalDebit > 0 || totalCredit > 0) && (
                            <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-xl text-sm font-bold text-center">
                                ⚠️ {t('Debits must equal Credits!')} (Diff: {fmt(Math.abs(totalDebit - totalCredit))})
                            </div>
                        )}
                        <button type="submit" disabled={!isBalanced} className={`w-full py-3 rounded-xl font-bold text-lg text-white transition ${isBalanced ? 'bg-indigo-600 hover:bg-indigo-700 shadow-lg' : 'bg-gray-300 cursor-not-allowed'}`}>
                            {t('Post Journal Entry')}
                        </button>
                    </form>
                </Modal>

                <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @media print {
                    .no-print { display: none !important; }
                    #ledger-print-root { display: block !important; }
                    @page { margin: 15mm; size: A4; }
                }
            `}</style>
            </div>
        </>
    );
};

export default JournalEntry;
